import {
  BottomSheet,
  BottomSheetIllustrationContent,
  type IllustrationType,
  LoadingSpinner,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { useGoToStripeExpressSignIn } from "@src/appV2/redesign/Accounts/PayoutSettings/hooks/useGoToStripeExpressSignIn";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { useEffect, useRef } from "react";

export type ProfileUpdateType = "EMAIL" | "PHONE";

export interface ProfileUpdateBottomSheetProps {
  modalState: UseModalState;
  updateType: ProfileUpdateType;
  onClose: () => void;
}

const ContentConfigByUpdateType: Record<
  ProfileUpdateType,
  { illustrationType: IllustrationType; title: string; description: string }
> = {
  EMAIL: {
    illustrationType: "mail-person",
    title: "You've updated your email address",
    description:
      "Please update your Stripe account as well to avoid any future interruptions to payments from Clipboard.",
  },
  PHONE: {
    illustrationType: "phone",
    title: "You've updated your phone number",
    description:
      "Please update your Stripe account as well to avoid any future interruptions to payments from Clipboard.",
  },
};

export function ProfileUpdateBottomSheet(props: ProfileUpdateBottomSheetProps) {
  const { modalState, updateType, onClose } = props;
  const didGoToStripe = useRef(false);
  const { goToStripeExpressSignIn, isGoToStripeExpressSignInLoading } =
    useGoToStripeExpressSignIn();

  useEffect(() => {
    if (modalState.modalIsClosed) {
      didGoToStripe.current = false;
    }
  }, [modalState.modalIsClosed]);

  useEffect(() => {
    if (!isGoToStripeExpressSignInLoading && didGoToStripe.current) {
      onClose();
    }
  }, [isGoToStripeExpressSignInLoading, onClose]);

  const contentConfig = ContentConfigByUpdateType[updateType];

  return (
    <BottomSheet
      modalState={modalState}
      header={<BottomSheetHeader onClose={onClose} />}
      footer={
        <DialogFooter orientation="vertical">
          <Button
            fullWidth
            sx={(theme) => ({ marginBottom: theme.spacing(2) })}
            disabled={isGoToStripeExpressSignInLoading}
            onClick={() => {
              didGoToStripe.current = true;
              goToStripeExpressSignIn();
            }}
          >
            {isGoToStripeExpressSignInLoading ? (
              <LoadingSpinner size="small" />
            ) : (
              "Take me to Stripe"
            )}
          </Button>
        </DialogFooter>
      }
      onClose={onClose}
    >
      <BottomSheetIllustrationContent
        illustrationType={contentConfig.illustrationType}
        title={contentConfig.title}
        description={contentConfig.description}
      />
    </BottomSheet>
  );
}
